import { apiCustomer, apiMerchant } from "@/api"
import { formatDecimalBR } from "@/lib/format-decimal-br"
import { refMatchesCartLine } from "@/lib/free-product-cart-item"
import { tenantWallClock } from "@/lib/scheduling/normalize-hours"
import type { Cart, CartItem } from "@/types"
import type { Discount } from "@/types/external"
import type {
  ActiveBenefits,
  Benefit,
  BenefitAvailability,
  CatalogItemRef,
  CustomerBenefits,
  EntitlementGrant,
} from "@/types/loyalty-benefits"

type BenefitCtx = {
  servicePrice: number
  cart: Cart
  at?: Date | readonly Date[]
  timeZone?: string | null
}

function claimInstants(at?: Date | readonly Date[]): Date[] {
  if (!at) return [new Date()]
  if (at instanceof Date) return [at]
  return [...at]
}

/**
 * The catalog product id (items.id) of a cart line — the same value
 * build-checkout-cart-items sends as the order item's `id`, which becomes the
 * order line's MerchantItemId. NOT `cartItem.id`, which is a browser-local
 * line id that never leaves this tab.
 */
function catalogItemId(cartItem: CartItem): string {
  return cartItem.item?.id || cartItem.itemOffer?.itemId || ""
}

export const LOYALTY_POINTS_CODE = "LOYALTY_POINTS"
export const LOYALTY_PREFIX = "LOYALTY_"

/**
 * Whether `at` falls inside the benefit window, on the merchant's wall clock.
 * Empty or absent blocks are always open. A day with no shifts is open all day.
 * Shift bounds are inclusive "HH:MM:SS", matching loyalty's AllowsAt.
 */
export function isBenefitAvailableAt(
  availability: BenefitAvailability | null | undefined,
  at: Date = new Date(),
  timeZone?: string | null,
): boolean {
  const blocks = availability?.blocks
  if (!blocks || blocks.length === 0) return true

  const clock = tenantWallClock(at, timeZone)
  return blocks.some((block) => {
    if (!block.days.includes(clock.day)) return false
    if (!block.schedules || block.schedules.length === 0) return true
    return block.schedules.some((shift) => clock.time >= shift.start && clock.time <= shift.end)
  })
}

/**
 * Redemption gate for points and already-unlocked grants. A window restricts
 * redemption only when `redeemOnlyWithinAvailability` is set; earning windows
 * without that flag still redeem.
 */
export function redemptionAllowedAt(
  availability: BenefitAvailability | null | undefined,
  at: Date = new Date(),
  timeZone?: string | null,
): boolean {
  if (!availability?.redeemOnlyWithinAvailability) return true
  return isBenefitAvailableAt(availability, at, timeZone)
}

/**
 * The merchant's active benefit mechanics (customer-independent). Served as
 * PUBLIC store info by merchant-service-gateway (like active coupons), so the
 * storefront can read it without a customer token. apiMerchant base already
 * ends in /merchants.
 */
export async function fetchActiveBenefits(merchantId: string): Promise<ActiveBenefits> {
  const { data } = await apiMerchant.get<ActiveBenefits>(
    `/${merchantId}/loyalties/active-benefits`,
  )
  return data
}

/** Concise PT-BR label for a benefit, shown as a storefront badge. */
export function benefitLabel(b: Benefit): string {
  if (b.freeProduct) return "Produto grátis"
  if (b.orderDiscount) return `R$ ${formatDecimalBR(b.orderDiscount.amount.value)} OFF`
  if (b.shipping) return b.shipping.type === "FREE_SHIPPING" ? "Frete grátis" : "Desconto no frete"
  return "Promoção"
}

/**
 * The headline benefit a trigger item unlocks: the best (highest-spend) tier of
 * the active item-promo whose trigger matches the item — by POS code or, for
 * items with no POS code, by catalog id. null if the item is not a trigger of
 * any active promo.
 */
export function triggerItemBenefit(
  active: ActiveBenefits | null | undefined,
  externalCode?: string,
  itemId?: string,
): { label: string; minSpend: number } | null {
  if (!active || (!externalCode && !itemId)) return null
  const promo = (active.itemPromos ?? []).find(
    (p) =>
      p.active &&
      isBenefitAvailableAt(p.availability) &&
      ((!!externalCode && p.triggerItem.externalCode === externalCode) ||
        (!!itemId && p.triggerItem.itemId === itemId)),
  )
  if (!promo || promo.tiers.length === 0) return null
  const top = [...promo.tiers].sort((a, b) => b.minItemSpend.value - a.minItemSpend.value)[0]
  return { label: benefitLabel(top.benefit), minSpend: top.minItemSpend.value }
}

/**
 * The customer's claimable entitlements + progress + points snapshot for a
 * merchant. customer_id is taken from the auth token by the gateway.
 */
export async function fetchCustomerBenefits(merchantId: string): Promise<CustomerBenefits> {
  const { data } = await apiCustomer.get<CustomerBenefits>(
    `/loyalties/by-merchant/${merchantId}/benefits`,
  )
  return data
}

/**
 * resolveBenefitDiscount turns one benefit + its LOYALTY_* code into the Open
 * Delivery discount entry. The amount is derived from the benefit so the order
 * totals reconcile:
 *  - orderDiscount  → fixed amount, target CART
 *  - free shipping  → whole delivery fee, target DELIVERY_FEE
 *  - shipping disc. → fixed amount capped at the fee, target DELIVERY_FEE
 *  - free product   → the matching cart item's unit price, target ITEM
 * Returns null when the amount can't be resolved (e.g. the free product is not
 * in the cart) — the benefit simply can't be applied on this order yet.
 */
export function resolveBenefitDiscount(
  benefit: Benefit,
  discountCode: string,
  ctx: BenefitCtx,
): { discount: Discount; value: number } | null {
  const entry = (
    value: number,
    target: Discount["target"],
    targetId?: string,
  ): { discount: Discount; value: number } | null => {
    if (value <= 0) return null
    return {
      value,
      discount: {
        amount: { value, currency: "BRL" },
        target,
        targetId,
        sponsorshipValues: [
          { name: "MERCHANT", amount: { value, currency: "BRL" }, discountCode },
        ],
      },
    }
  }

  if (benefit.orderDiscount) {
    const capped = Math.min(benefit.orderDiscount.amount.value, ctx.cart.totalPrice)
    return entry(capped, "CART")
  }
  if (benefit.shipping) {
    if (benefit.shipping.type === "FREE_SHIPPING") {
      return entry(ctx.servicePrice, "DELIVERY_FEE")
    }
    const capped = Math.min(benefit.shipping.amount?.value ?? 0, ctx.servicePrice)
    return entry(capped, "DELIVERY_FEE")
  }
  if (benefit.freeProduct) {
    const ref = benefit.freeProduct.item
    const matches = ctx.cart.items.filter((ci) => refMatchesCartLine(ref, ci))
    if (matches.length === 0) return null

    const match = matches.find((ci) => ci.quantity === 1) ?? matches[0]

    return entry(match.itemTotalPrice, "ITEM", catalogItemId(match))
  }
  return null
}

/** An unlocked entitlement resolves like any benefit, keyed by its grant code. */
export function resolveEntitlementDiscount(
  grant: EntitlementGrant,
  ctx: BenefitCtx,
): { discount: Discount; value: number } | null {
  return resolveBenefitDiscount(grant.benefit, grant.discountCode, ctx)
}

/** buildBenefitDiscounts maps selected entitlements to discount entries. */
export function buildBenefitDiscounts(
  grants: EntitlementGrant[],
  ctx: BenefitCtx,
): { discounts: Discount[]; totalValue: number } {
  const discounts: Discount[] = []
  let totalValue = 0
  for (const grant of grants) {
    const resolved = resolveEntitlementDiscount(grant, ctx)
    if (resolved) {
      discounts.push(resolved.discount)
      totalValue += resolved.value
    }
  }
  return { discounts, totalValue }
}

/** Sum of the order's spend on a trigger item (POS code, or catalog id when it has none). */
function triggerItemSpend(cart: Cart, ref: CatalogItemRef): number {
  return cart.items
    .filter((ci) => refMatchesCartLine(ref, ci))
    .reduce((sum, ci) => sum + (ci.itemTotalPrice ?? ci.unitPrice * ci.quantity), 0)
}

/**
 * computeAutoBenefitDiscounts derives the benefits the cart ALREADY qualifies
 * for, with no customer action: the active cart-threshold (items subtotal ≥
 * minimum) and the best satisfied tier of each active SINGLE_ORDER item-promo,
 * and only while that mechanic's window holds at every instant in `ctx.at`
 * (placement, plus the scheduled ready time when the order proposes one), on
 * the merchant clock. Outside the window the mechanic is omitted — loyalty
 * would reject the claim with BENEFIT_OUTSIDE_AVAILABILITY_WINDOW.
 */
export function computeAutoBenefitDiscounts(
  active: ActiveBenefits | null | undefined,
  ctx: BenefitCtx,
): { discounts: Discount[]; totalValue: number } {
  const discounts: Discount[] = []
  let totalValue = 0
  if (!active) return { discounts, totalValue }

  const instants = claimInstants(ctx.at)
  const open = (availability: BenefitAvailability | null | undefined) =>
    instants.every((at) => isBenefitAvailableAt(availability, at, ctx.timeZone))

  const push = (r: { discount: Discount; value: number } | null) => {
    if (r) {
      discounts.push(r.discount)
      totalValue += r.value
    }
  }

  const ct = active.cartThreshold
  if (
    ct &&
    ct.active &&
    open(ct.availability) &&
    ctx.cart.totalPrice >= ct.minItemsSubtotal.value
  ) {
    push(resolveBenefitDiscount(ct.benefit, `LOYALTY_CART_THRESHOLD:${ct.id}`, ctx))
  }

  for (const promo of active.itemPromos ?? []) {
    if (!promo.active || promo.basis !== "SINGLE_ORDER" || !open(promo.availability)) continue
    const spend = triggerItemSpend(ctx.cart, promo.triggerItem)
    if (spend <= 0) continue
    const best = [...promo.tiers]
      .filter((t) => t.minItemSpend.value <= spend)
      .sort((a, b) => b.minItemSpend.value - a.minItemSpend.value)[0]
    if (!best) continue
    push(resolveBenefitDiscount(best.benefit, `LOYALTY_ITEM_PROMO:${promo.id}:${best.id}`, ctx))
  }

  return { discounts, totalValue }
}

function sourceAvailability(
  active: ActiveBenefits | null | undefined,
  grant: EntitlementGrant,
): BenefitAvailability | null | undefined {
  if (!active) return undefined
  if (grant.source === "RECURRENCE" && active.recurrence?.id === grant.sourceConfigId) {
    return active.recurrence.availability
  }
  if (grant.source === "ITEM_SPEND") {
    return active.itemPromos?.find((promo) => promo.id === grant.sourceConfigId)?.availability
  }
  return undefined
}

/**
 * Grants the customer may still redeem at `at`. A source that restricts
 * redemption to its window is dropped while that window is closed, so the
 * order never carries a claim loyalty would refuse.
 */
export function openEntitlements(
  active: ActiveBenefits | null | undefined,
  grants: EntitlementGrant[],
  at: Date | readonly Date[] = new Date(),
  timeZone?: string | null,
): EntitlementGrant[] {
  const instants = claimInstants(at)
  return grants.filter((grant) =>
    instants.every((instant) =>
      redemptionAllowedAt(sourceAvailability(active, grant), instant, timeZone),
    ),
  )
}

/** Discount code of an automatic rule promotion (contract C6). */
export const LOYALTY_RULE_PROMO_PREFIX = "LOYALTY_RULE_PROMO:"

function discountCodeOf(discount: Discount): string {
  return discount.sponsorshipValues[0]?.discountCode ?? ""
}

/** Rule-promotion discounts grouped per promotion rule: one benefit may land on several lines. */
function groupByCode(discounts: readonly Discount[]): Discount[][] {
  const groups = new Map<string, Discount[]>()
  for (const discount of discounts) {
    const code = discountCodeOf(discount)
    groups.set(code, [...(groups.get(code) ?? []), discount])
  }
  return [...groups.values()]
}

function groupValue(group: readonly Discount[]): number {
  return group.reduce((sum, discount) => sum + discount.amount.value, 0)
}

/**
 * assembleBenefitDiscounts combines the auto-applied benefits (cart-threshold +
 * SINGLE_ORDER item-promos), the automatic rule promotions loyalty evaluated
 * (`LOYALTY_RULE_PROMO:*`, one benefit per promotion rule however many lines it
 * spans) and the customer's selected entitlements, honoring the merchant's
 * stacking policy. Under SINGLE_BENEFIT_PER_ORDER only the single
 * highest-value benefit is kept (points redemption is separate and always
 * allowed). A mechanic outside its window is left off the order.
 */
export function assembleBenefitDiscounts(params: {
  active: ActiveBenefits | null | undefined
  selectedEntitlements: EntitlementGrant[]
  servicePrice: number
  cart: Cart
  at?: Date | readonly Date[]
  timeZone?: string | null
  rulePromotionDiscounts?: readonly Discount[]
}): { discounts: Discount[]; totalValue: number } {
  const at = params.at ?? new Date()
  const ctx: BenefitCtx = {
    servicePrice: params.servicePrice,
    cart: params.cart,
    at,
    timeZone: params.timeZone,
  }
  const auto = computeAutoBenefitDiscounts(params.active, ctx)
  const ent = buildBenefitDiscounts(
    openEntitlements(params.active, params.selectedEntitlements, at, params.timeZone),
    ctx,
  )
  let groups: Discount[][] = [
    ...ent.discounts.map((discount) => [discount]),
    ...auto.discounts.map((discount) => [discount]),
    ...groupByCode(params.rulePromotionDiscounts ?? []),
  ]

  if (params.active?.stackingPolicy === "SINGLE_BENEFIT_PER_ORDER" && groups.length > 1) {
    groups = [groups.reduce((best, group) => (groupValue(group) > groupValue(best) ? group : best))]
  }

  const discounts = groups.flat()
  const totalValue = discounts.reduce((sum, d) => sum + d.amount.value, 0)
  return { discounts, totalValue }
}

/**
 * The share of a set of benefit discounts that abates the items subtotal — the
 * ITEM and CART targets. DELIVERY_FEE benefits are left out: they abate the fee,
 * which points redemption never reaches.
 */
export function benefitItemsAbatement(discounts: Discount[]): number {
  return discounts
    .filter((d) => d.target === "ITEM" || d.target === "CART")
    .reduce((sum, d) => sum + d.amount.value, 0)
}
